import { useCallback, useEffect, useState } from 'react';
import { debounce } from 'es-toolkit';

import { AdornedInput, Form, Icon, IconButton, Spinner } from '@zillow/constellation-10';
import { IconCloseCircleFilled, IconSearchFilled } from '@zillow/constellation-icons';

import type React from 'react';

export type SearchBoxProps = {
  'aria-label': string;
  initialText?: string;
  placeholder?: string;
  onSearch?: (value: string) => void;
  onSubmit?: (value: string) => void;
  isSearching?: boolean;
  iconSize?: 'sm' | 'md' | 'lg';
  debounceMilliseconds?: number;
  disabled?: boolean;
};

const SearchBox = ({
  'aria-label': ariaLabel,
  initialText = '',
  onSearch = () => {},
  onSubmit = () => {},
  iconSize: size = 'sm',
  placeholder = 'Search',
  isSearching = false,
  debounceMilliseconds = 500,
  disabled = false,
}: SearchBoxProps) => {
  const [searchText, setSearchText] = useState(initialText);

  // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  const handleSearchDebounced = useCallback(debounce(onSearch, debounceMilliseconds), [onSearch, debounceMilliseconds]);

  useEffect(() => {
    handleSearchDebounced(searchText);
    return () => handleSearchDebounced.cancel();
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [searchText]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const clearSearchText = () => {
    setSearchText('');
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (onSubmit) onSubmit(searchText);
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') clearSearchText();
  };

  const renderAdornment = () =>
    searchText ? (
      <IconButton
        tone="neutral"
        emphasis="bare"
        size={size}
        title="Clear"
        onClick={clearSearchText}
        css={{ backgroundColor: 'transparent', width: '100%', height: '100%' }}
      >
        <IconCloseCircleFilled data-testid="SearchBox-CloseButton" />
      </IconButton>
    ) : (
      <Icon size={size}>
        <IconSearchFilled data-testid="SearchBox-SearchIcon" />
      </Icon>
    );

  return (
    <Form onSubmit={handleSubmit}>
      <AdornedInput
        data-testid="SearchBox-Component"
        disabled={disabled}
        input={
          <AdornedInput.Input
            aria-label={ariaLabel}
            placeholder={placeholder}
            onChange={handleChange}
            onKeyUp={handleKeyUp}
            value={searchText}
          />
        }
        endAdornment={
          <AdornedInput.Adornment>
            {isSearching ? <Spinner size={size} data-testid="SearchBox-Spinner" /> : renderAdornment()}
          </AdornedInput.Adornment>
        }
      />
    </Form>
  );
};

export { SearchBox };
export default SearchBox;
